export enum AgentWorkflowRunStatus {
  QUEUED = "QUEUED",
  RUNNING = "RUNNING",
  WAITING = "WAITING",
  REQUIRES_REVIEW = "REQUIRES_REVIEW",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
  CANCELLED = "CANCELLED",
}

/**
 * Raw DB column values for `agent_workflow_run_tasks.status`. Legacy rows
 * still use `PENDING_REVIEW` / `APPROVED`; the new task-driven flow
 * additionally uses `PENDING` (replacing the old transient `DRAFT`).
 * Consumers working in the new-model task API should use `AgentTaskStatus`
 * below — this enum is only the database representation.
 */
export enum AgentWorkflowRunTaskStatus {
  PENDING = "PENDING",
  PENDING_REVIEW = "PENDING_REVIEW",
  APPROVED = "APPROVED",
  CANCELLED = "CANCELLED",
}

/**
 * Public-facing task lifecycle used by `create_or_update_task`, the
 * messages endpoint, and the frontend:
 *
 * - `PENDING` — planned but not ready for action yet.
 * - `IN_PROGRESS` — task is active for a user or the agent.
 * - `COMPLETED` / `CANCELLED` — terminal.
 *
 * `IN_PROGRESS` and `COMPLETED` map onto the existing DB values
 * `PENDING_REVIEW` and `APPROVED` respectively, so legacy rows continue to
 * work without a data migration. Use the `toAgentTaskStatus` /
 * `toAgentWorkflowRunTaskStatus` helpers to translate.
 */
export enum AgentTaskStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

/**
 * Translate a DB status row into the public task lifecycle.
 */
export function toAgentTaskStatus(
  status: AgentWorkflowRunTaskStatus,
): AgentTaskStatus {
  switch (status) {
    case AgentWorkflowRunTaskStatus.PENDING:
      return AgentTaskStatus.PENDING;
    case AgentWorkflowRunTaskStatus.PENDING_REVIEW:
      return AgentTaskStatus.IN_PROGRESS;
    case AgentWorkflowRunTaskStatus.APPROVED:
      return AgentTaskStatus.COMPLETED;
    case AgentWorkflowRunTaskStatus.CANCELLED:
      return AgentTaskStatus.CANCELLED;
  }
}

/**
 * Translate a public task lifecycle value into the DB column value.
 */
export function toAgentWorkflowRunTaskStatus(
  status: AgentTaskStatus,
): AgentWorkflowRunTaskStatus {
  switch (status) {
    case AgentTaskStatus.PENDING:
      return AgentWorkflowRunTaskStatus.PENDING;
    case AgentTaskStatus.IN_PROGRESS:
      return AgentWorkflowRunTaskStatus.PENDING_REVIEW;
    case AgentTaskStatus.COMPLETED:
      return AgentWorkflowRunTaskStatus.APPROVED;
    case AgentTaskStatus.CANCELLED:
      return AgentWorkflowRunTaskStatus.CANCELLED;
  }
}

/**
 * Discriminates how a task is structured. `LEGACY` is reserved for the
 * pre-refactor review-approval flow (rendered through RichActionPanel);
 * `general` and `action` are the new task-driven primitives surfaced via
 * the task list UI.
 */
export enum AgentWorkflowRunTaskType {
  LEGACY = "legacy",
  GENERAL = "general",
  ACTION = "action",
}
